import { Input as NextInput } from '@nextui-org/react';
import React, { ComponentProps, forwardRef } from 'react';
import styled from 'styled-components';

import { ThemeProp } from '../../theme/Themes';
import HelpTooltip from '../HelpTooltip';

export interface InputProps extends ComponentProps<typeof NextInput> {
  error?: string;
  $requiredLabel?: boolean;
  noMarginBottom?: boolean;
}

export const getInputContainerStyle = (props) => {
  const isError = props.status === 'error';
  return {
    height: '2.625rem',
    border: `$borderWeights$normal solid ${
      isError ? props.theme.colors.system.error : props.theme.colors.neutrals.c300
    }`,
    borderRadius: '$xs',
    backgroundColor: 'transparent',
    ...props.css?.['& .nextui-input-container'],
  };
};

const getInputWrapperStyle = (props) => {
  return {
    background: '$inputBackground',
    borderRadius: '$xs',
    paddingLeft: '0',
    opacity: '1',
    ...props.css?.['& .nextui-input-wrapper'],
  };
};

const getInputStyle = (props) => {
  const isError = props.status === 'error';
  return {
    ...(isError && { color: '$text' }),
    fontSize: '$input',
    lineHeight: '$sm2',

    '&:disabled': {
      background: `${props.theme.colors.neutrals.c100} !important`,
      color: `${props.theme.colors.content.disabled} !important`,
      opacity: '1 !important',
      '-webkit-text-fill-color': `${props.theme.colors.content.disabled} !important`,
    },
    '&:focus::placeholder': {
      opacity: 1,
    },
    ...props.css?.['& .nextui-input'],
  };
};

const applyStyle = (component: any) => {
  return styled(component).attrs((props) => {
    const isError = props.status === 'error';
    return {
      ...props,
      animated: false,
      css: {
        '&': {
          marginBottom: isError ? '$5' : props.noMarginBottom ? '0' : '$8',
          width: '100%',
        },
        '& .nextui-input-container--disabled': {
          background: '$disabledBackground !important',
          color: `${props.theme.colors.content.disabled} !important`,
        },
        '& .nextui-input-content--left': {
          paddingRight: props.noRightPaddingIconLeft && 0,
        },
        '& .nextui-input': {
          margin: props.iconLeft ? '0' : '0.5rem',
          '&:not(:placeholder-shown)': {
            marginRight: props.clearable ? '$12' : undefined,
          },
        },
        '& input': getInputStyle(props),
        '& .nextui-input-block-label:after': {
          content: props.$requiredLabel ? ' *' : '',
          paddingLeft: '0',
        },
        '& .nextui-input-block-label': {
          color: props.labelColor ? props.labelColor : props.theme.colors.content.default,
          fontSize: '$sm',
          lineHeight: '$xs',
          paddingLeft: '0',
        },
        '& .nextui-input-container': getInputContainerStyle(props),
        '& .nextui-input-container--hover': {
          border: `1px solid ${isError ? props.theme.colors.system.error : props.theme.colors.neutrals.c500}`,
        },
        '& .nextui-input-container .nextui-input-wrapper': getInputWrapperStyle(props),
        '.nextui-input-wrapper--disabled': {
          background: `${props.theme.colors.neutrals.c100} !important`,
          color: '$textSecondary !important',
          opacity: '1 !important',
        },
        '& .nextui-input-helper-text-container': {
          display: isError ? 'block' : 'none',
          position: 'unset',
          marginTop: '$1',
        },
        '& .nextui-input-helper-text-container p': {
          fontSize: 'calc($sm - 1px)',
          lineHeight: '$xs3',
        },
        'input:-webkit-autofill': {
          '-webkit-text-fill-color': props.theme.colors.content.default,
          caretColor: props.theme.colors.content.default,
        },
        'input:-webkit-autofill:hover': {
          '-webkit-text-fill-color': props.theme.colors.content.default,
          caretColor: props.theme.colors.content.default,
        },
        'input:-webkit-autofill:focus': {
          '-webkit-text-fill-color': props.theme.colors.content.default,
          caretColor: props.theme.colors.content.default,
        },
        ...props.css,
      },
    };
  })`
    @supports (-webkit-touch-callout: none) {
      /* CSS specific to iOS devices: large password dots fix */
      & input[type='password']:not(:placeholder-shown) {
        font: small-caption;
        font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
      }
    }

    & input::placeholder {
      color: ${({ theme }: ThemeProp) => theme.colors.content.subdued.high};
    }
  `;
};

const Input = applyStyle(NextInput);

Input.Password = applyStyle(NextInput.Password);

const HelperWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const InputErrorWrapper = forwardRef<typeof Input, any>((props, ref) => {
  const label = props.label;

  const allowOnlyLetters = props.allowOnlyLetters;

  const onChangeOnlyLetter = (event: any) => {
    event.target.value = event.target.value.replace(/[^a-zA-Z-. ]/g, '');
    props.onChange?.(event);
  };

  const onKeyDownOnlyLetter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^[a-zA-Z-. ]*$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const onChange = allowOnlyLetters ? onChangeOnlyLetter : props.onChange;
  const onKeyDown = allowOnlyLetters ? onKeyDownOnlyLetter : props.onKeyDown;

  const labelWithHelper = (
    <HelperWrapper>
      {label} {<HelpTooltip $helpText={props.helpText} />}
    </HelperWrapper>
  );

  return (
    <>
      <Input
        {...props}
        label={props.helpText ? labelWithHelper : label}
        ref={ref}
        type={props.type || 'text'}
        placeholder={props.placeholder || ''}
        contentLeft={props.iconLeft || null}
        status={props.error ? 'error' : 'default'}
        helperColor={props.error ? 'error' : 'default'}
        helperText={props.error}
        onChange={onChange}
        onKeyDown={onKeyDown}
        contentRight={props.contentRight}
      />
    </>
  );
}) as any;

InputErrorWrapper.Password = (props) => {
  return (
    <Input.Password
      {...props}
      status={props.error ? 'error' : 'default'}
      helperColor={props.error ? 'error' : 'default'}
      helperText={props.error}
    />
  );
};

export default InputErrorWrapper;
